import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const SearchableSelect = ({
  id,
  label,
  groups = null,
  options = null,
  value,
  onChange,
  placeholder = 'Suchen...',
}) => {
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const flatOptions = useMemo(() => {
    if (options) {
      return options;
    }
    if (!groups) {
      return [];
    }
    return groups.flatMap((group) =>
      group.options.map((option) => ({
        ...option,
        group: group.label,
      })),
    );
  }, [groups, options]);

  const selectedLabel =
    flatOptions.find((option) => option.value === value)?.label || '';

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      if (groups) {
        return { groups };
      }
      return { options: flatOptions };
    }

    const matches = flatOptions.filter((option) => {
      const haystack = `${option.group || ''} ${option.label}`.toLowerCase();
      return haystack.includes(term);
    });

    if (groups) {
      const grouped = {};
      matches.forEach((option) => {
        const groupLabel = option.group || 'Other';
        if (!grouped[groupLabel]) {
          grouped[groupLabel] = [];
        }
        grouped[groupLabel].push(option);
      });

      return {
        groups: Object.entries(grouped).map(([groupLabel, groupOptions]) => ({
          label: groupLabel,
          options: groupOptions,
        })),
      };
    }

    return { options: matches };
  }, [search, groups, flatOptions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
        setSearch('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open) {
      searchRef.current?.focus();
    }
  }, [open]);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setOpen(false);
    setSearch('');
  };

  const hasResults = filtered.groups
    ? filtered.groups.some((group) => group.options.length > 0)
    : filtered.options.length > 0;

  return (
    <label className="SearchableSelect-label" htmlFor={`${id}-trigger`}>
      <span className="SearchableSelect-labelText">{label}</span>
      <div className="SearchableSelect" ref={containerRef}>
        <button
          type="button"
          id={`${id}-trigger`}
          className="SearchableSelect-trigger"
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={() => setOpen((current) => !current)}
        >
          {selectedLabel || '—'}
        </button>
        {open && (
          <div className="SearchableSelect-dropdown">
            <input
              ref={searchRef}
              type="text"
              className="SearchableSelect-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={placeholder}
            />
            <div className="SearchableSelect-options" role="listbox" id={id}>
              {!hasResults && (
                <div className="SearchableSelect-empty">Keine Treffer</div>
              )}
              {filtered.groups
                ? filtered.groups.map((group) => (
                    <div
                      key={group.label}
                      className="SearchableSelect-groupBlock"
                    >
                      <div className="SearchableSelect-group">
                        {group.label}
                      </div>
                      {group.options.map((option) => (
                        <button
                          type="button"
                          key={option.value}
                          role="option"
                          aria-selected={option.value === value}
                          className={`SearchableSelect-option ${
                            option.value === value ? 'selected' : ''
                          }`}
                          onClick={() => handleSelect(option.value)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  ))
                : filtered.options.map((option) => (
                    <button
                      type="button"
                      key={option.value}
                      role="option"
                      aria-selected={option.value === value}
                      className={`SearchableSelect-option ${
                        option.value === value ? 'selected' : ''
                      }`}
                      onClick={() => handleSelect(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
            </div>
          </div>
        )}
      </div>
    </label>
  );
};

SearchableSelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired,
        }),
      ).isRequired,
    }),
  ),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

export default SearchableSelect;
